import React from "react";

import { Alert, Card, Form, Col, Row, Button, Spinner } from "react-bootstrap";

import {
  ElementsConsumer,
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import PropTypes from "prop-types";
import ReciprocityAgreement from "../ReciprocityAgreement/ReciprocityAgreement";


import config from "../../config";
import { devLog, isPaymentRegion } from "../../utils/helpers";
import {
  getSession,
  saveSession,
  userLogin,
  userRegister,
  getUserFromToken,
  postOrder,
  updateOrder,
} from "../../utils";

import styles from "./CreditCardForm.module.css";

const elementOptions = {
  classes: {
    base: "form-control",
  },
  style: {
    base: {
      fontSize: "16px",
      fontWeight: 400,
      // lineHeight removed - use padding on container instead per Stripe recommendation
    },
  },
};

class CardElementForm extends React.Component {
  constructor(props) {
    super(props);

    const registration = this.props.order?.registration || {};
    const region = registration.municipality?.regionId || 
                   registration.ruralArea?.regionId || 
                   'rgn_0';
    
    devLog('CreditCardForm constructor - region:', region);

    this.state = {
      name: "",
      postalCode: registration.postalCode || "",
      phone: registration.phone || "",
      email: "",
      password: "",
      region,
      session: getSession(),
      paymentIntent: null,
      loading: false,
      errorMsg: "",
      toggleReciprocityAgreement: false,
      agreeToRecipAgreement: false,
      userType: registration.type || 'homeowner',
    };
  }

  getUserSession = async (email, password) => {
    let token = null;
    let errorMsg = null;

    devLog('getUserSession - region:', this.state.region);

    // Try registering new user first
    try {
      token = await userRegister(email, password, this.state.region);
    } catch (error) {
      if (!error.message?.includes("already registered")) {
        errorMsg = error.message || "Unknown error registering new user";
      }
    }

    // Try logging in existing user
    if (!token && !errorMsg) {
      try {
        token = await userLogin(email, password, this.state.region);
      } catch (error) {
        errorMsg = error.message || "Unknown error logging in user";
      }
    }

    // Save session if token obtained
    if (token) {
      const response = await getUserFromToken(token.token);
      const session = saveSession(response.id, response.email, token.token);
      devLog("User session saved");
      return { session, error: null };
    }
    
    return { session: null, error: errorMsg };
  };

  toggleReciprocityAgreement = (event) => {
    event?.preventDefault();
    this.setState({ toggleReciprocityAgreement: !this.state.toggleReciprocityAgreement });
  };

  handleRecipAgreementChange = () => {
    this.setState({ agreeToRecipAgreement: !this.state.agreeToRecipAgreement });
  };

  // Stripe amounts are in the currency's smallest unit
  getStripeAmount = (total, currency) => {
    const zeroDecimalCurrencies = [
      "bif",
      "clp",
      "djf",
      "gnf",
      "jpy",
      "kmf",
      "krw",
      "mga",
      "pyg",
      "rwf",
      "ugx",
      "vnd",
      "vuv",
      "xaf",
      "xof",
      "xpf",
    ];
    if (zeroDecimalCurrencies.includes(currency)) return total;
    return total * 100;
  };

  setErrorMsg = (errorMsg) => {
    this.setState({
      loading: false,
      errorMsg: errorMsg || "",
    });
  };

  checkIfRegionCollectingPayments() {
    // Check if the region is configured to collect payments
    return isPaymentRegion(this.state.region);
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    this.setState({ loading: true, errorMsg: "" });

    devLog("Submitting form:", this.state);

    this.setState({ session: { userEmail: this.state.email } });
    this.props.order.email = this.state.email;

    // Only collect payments if in payment-enabled region (rgn_5 or rgn_3)
    if (this.checkIfRegionCollectingPayments()) {
      if (!this.state.session) {
        // Use the email and password to try and login or create a user and generate a session
        const result = await this.getUserSession(this.state.email, this.state.password);
        if (result.error) {
          this.setErrorMsg(result.error);
          return;
        }
        this.setState({ session: result.session });
      }

      let order = this.props.order;
      order.amount = this.getStripeAmount(order.total, order.currency);
      let paymentIntentVariable;
      if (!this.state.paymentIntent) {
        // Create new payment intent
        const result = await postOrder({
          amount: order.amount,
          currency: order.currency,
          description: order.description,
          receipt_email: order.email,
          regionId: this.state.region,
          metadata: {
            userId: this.state.session.userId,
            regionId: this.state.region,
          },
        });

        if (result.err) {
          this.setErrorMsg(`Order error: ${result.err.code} ${result.err.param}`);
          return;
        }

        if (!result?.data?.client_secret) {
          this.setErrorMsg("Order error: failed to get client secret");
          return;
        }
        
        devLog("Payment intent created:", result.data);
        paymentIntentVariable = result.data;
        this.setState({ paymentIntent: result.data });
      } else {
        // Update existing payment intent if necessary
        const properties = {
          receipt_email: order.email,
          ...(order.amount !== this.state.paymentIntent.amount && { amount: order.amount }),
          ...(order.currency !== this.state.paymentIntent.currency && { currency: order.currency }),
          ...(order.description !== this.state.paymentIntent.description && { description: order.description }),
        };

        if (Object.keys(properties).length > 1) { // More than just receipt_email
          const result = await updateOrder(this.state.paymentIntent.id, properties, this.state.region);

          if (result.err) {
            this.setErrorMsg(`Order error: ${result.err.code} ${result.err.param}`);
            return;
          }

          if (!result.paymentIntent) {
            this.setErrorMsg("Order error: failed to update");
            return;
          }

          devLog("Payment intent updated:", result.paymentIntent);
          paymentIntentVariable = result.paymentIntent;
          this.setState({ paymentIntent: result.paymentIntent });
        } else {
          paymentIntentVariable = this.state.paymentIntent;
        }
      }
      // Send the card info to stripe
      const result = await this.props.stripe.confirmCardPayment(paymentIntentVariable.client_secret, {
        payment_method: {
          card: this.props.elements.getElement(CardNumberElement),
          billing_details: {
            name: this.state.name,
            email: this.state.email,
            phone: this.state.phone || null,
            address: {
              postal_code: this.state.postalCode,
            },
          },
        },
      });

      if (result.error) {
        this.setErrorMsg(result.error.message || result.error.code || "Unknown error. Please try again.");
      } else {
        devLog("Payment confirmed, submitting");
        this.props.submit({
          name: this.state.name,
          phone: this.state.phone,
          postalCode: this.state.postalCode,
          paymentIntent: paymentIntentVariable,
        });
      }
    } else {
      // Pledge-only submission
      devLog("Pledge submission");
      this.props.submit({
        name: this.state.name,
        phone: this.state.phone,
        postalCode: this.state.postalCode,
        pledgeOnly: true,
      });
    }
  };

  handleChange = (event) => {
    const { name, value } = event.currentTarget;
    if (name) this.setState({ [name]: value });
  };

  render = () => {
    const { submit, ...rest } = this.props;
    const isPaymentRegion = this.checkIfRegionCollectingPayments();

    return (
      <Card {...rest}>
        <Card.Header>{isPaymentRegion ? 'Join Reciprocity Today' : 'Pledge to join Reciprocity:'}</Card.Header>
        <Card.Body>
          <Form onSubmit={this.handleSubmit}>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="name">
                <Form.Label>Name *</Form.Label>
                <Form.Control required name="name" value={this.state.name} onChange={this.handleChange} />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="cardElement">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  required
                  name="email"
                  autoComplete="email"
                  value={this.state.email}
                  onChange={this.handleChange}
                />
              </Form.Group>
            </Row>
            {isPaymentRegion && (
              <>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="cardElement">
                    <Form.Label>Credit Card Number *</Form.Label>
                    <CardNumberElement required options={elementOptions} />
                  </Form.Group>
                </Row>
                <Row className="mb-3">
                  <Form.Group as={Col} xs={12} sm={6} className="mb-3 mb-sm-0" controlId="expiryDate">
                    <Form.Label>Expiry Date *</Form.Label>
                    <CardExpiryElement options={elementOptions} />
                  </Form.Group>
                  <Form.Group as={Col} xs={12} sm={6} controlId="cvc">
                    <Form.Label>Security Code *</Form.Label>
                    <CardCvcElement options={elementOptions} />
                  </Form.Group>
                </Row>
              </>
            )}
            <Row className="mb-3">
              <Form.Group as={Col} xs={12} sm={6} className="mb-3 mb-sm-0" controlId="postalCode">
                <Form.Label>Postal Code *</Form.Label>
                <Form.Control required name="postalCode" value={this.state.postalCode} onChange={this.handleChange} />
              </Form.Group>
              <Form.Group as={Col} xs={12} sm={6} controlId="phone">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control name="phone" value={this.state.phone} onChange={this.handleChange} />
              </Form.Group>
            </Row>
            <Row className='mb-3 mt-4' >
              <Form.Group as={Col} controlId="agreeToRecipAgreement">
                <Form.Check
                  required
                  type="checkbox"
                  name="agreeToRecipAgreement"
                  label= {
                    <span>
                      I consent to the <button className='btn btn-link align-baseline p-0' onClick={this.toggleReciprocityAgreement}>Reciprocity Agreement</button>*
                    </span>
                  }
                  checked={this.state.agreeToRecipAgreement}
                  onChange={this.handleRecipAgreementChange}
                />
                </Form.Group>
            </Row>
            <ReciprocityAgreement show={this.state.toggleReciprocityAgreement} onHide={this.toggleReciprocityAgreement} userType={this.state.userType} />
            <div className={styles.requiredTag}>* Required field</div>
            {this.state.errorMsg && <Alert variant="danger">{this.state.errorMsg}</Alert>}
            <Button 
              type="submit" 
              disabled={(isPaymentRegion && (!this.props.stripe || !this.props.elements)) || !this.state.agreeToRecipAgreement || this.state.loading}
            >
              {this.state.loading ? (
                <span>
                  <Spinner as="span" animation="grow" size="sm" /> {isPaymentRegion ? 'Processing Your Payment...' : 'Processing Your Pledge...'}
                </span>
              ) : (
                <span>{isPaymentRegion ? 'Make My Reciprocity Payment' : 'Pledge Now'}</span>
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    );
  };
}

const CreditCardForm = (props) => {
  const registration = props.order?.registration || {};
  const regionId = registration.municipality?.regionId || 
                   registration.ruralArea?.regionId || 
                   'rgn_0';
  
  // Get Stripe key for payment regions only (rgn_5, rgn_3)
  const stripeKeyMap = {
    'rgn_5': config.stripe_publishable_key_rgn5,
    'rgn_3': config.stripe_publishable_key_rgn3,
  };
  
  const stripeKey = stripeKeyMap[regionId] || null;
  
  if (!stripeKey && regionId !== 'rgn_0' && regionId !== 'rgn_1' && regionId !== 'rgn_2' && regionId !== 'rgn_4') {
    console.error(`Unknown region: ${regionId}`);
  }
  
  // Memoize stripePromise to prevent recreation on every render
  const stripePromise = React.useMemo(() => {
    return stripeKey ? loadStripe(stripeKey) : null;
  }, [stripeKey]);
  
  return (
    <Elements stripe={stripePromise}>
      <ElementsConsumer>
        {({ elements, stripe }) => <CardElementForm elements={elements} stripe={stripe} {...props} />}
      </ElementsConsumer>
    </Elements>
  );
};

CreditCardForm.propTypes = {
  order: PropTypes.object.isRequired,
  submit: PropTypes.func.isRequired,
};

export default CreditCardForm;
